import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Line } from 'react-chartjs-2';
import dayjs from 'dayjs';
import { LineChart, Ticket, ShoppingBag, Receipt, TicketPercent, Layers3, Megaphone } from 'lucide-react';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { Card, Grid, Label, Muted, PillarBlock, Section } from '../parts';
import { formatDate, formatNumber } from '../labels';

const POST_MARKER_COLOR = colors.goldLight;

export default function SalesTab({ analysis, pillarsByTab }) {
    const { result, previous } = analysis;
    const sales = result.sales;

    return (
        <>
            {pillarsByTab.map(pillar => (
                <Section key={pillar.key} icon={LineChart} title={pillar.title}>
                    <PillarBlock pillar={pillar} previousScore={previous?.pillars?.[pillar.key]} />
                </Section>
            ))}

            {!sales ? (
                <Card>
                    <Muted>
                        Você ainda não tem eventos encerrados na Whizr. Quando tiver, a próxima análise cruza as suas vendas com os seus posts.
                    </Muted>
                </Card>
            ) : (
                <>
                    <Insight>
                        <Megaphone size={18} color={panelAccent(colors.secondaryPurple)} />
                        <div>
                            <InsightTitle>{sales.verdict}</InsightTitle>
                            <Muted>{sales.insight}</Muted>
                        </div>
                    </Insight>

                    {sales.events.map(event => <EventSales key={event.event_id} event={event} />)}
                </>
            )}
        </>
    );
}

function EventSales({ event }) {
    const chart = useMemo(() => buildChart(event), [event]);
    const maxBatch = Math.max(1, ...event.batches.map(batch => batch.tickets));

    return (
        <Section
            icon={Ticket}
            title={event.name}
            subtitle={`Encerrado em ${formatDate(event.ended_on)} · vendas da Whizr cruzadas com os seus posts`}
        >
            <Grid $min='180px'>
                <Kpi icon={Ticket} label='Ingressos' value={formatNumber(event.tickets_sold)} />
                <Kpi icon={ShoppingBag} label='Pedidos' value={formatNumber(event.orders)} />
                <Kpi
                    icon={Receipt}
                    label='Ticket médio'
                    value={event.average_ticket > 0 ? `R$ ${formatNumber(event.average_ticket, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Gratuito'}
                />
            </Grid>

            <Card>
                <ChartHead>
                    <Label>Ingressos por dia</Label>
                    <Legend>
                        <LegendItem><Swatch style={{ background: panelAccent(colors.secondaryPurple) }} /> Vendas</LegendItem>
                        <LegendItem><Swatch $diamond style={{ background: POST_MARKER_COLOR }} /> Post de divulgação</LegendItem>
                    </Legend>
                </ChartHead>
                <ChartWrap>
                    <Line data={chart.data} options={chart.options} />
                </ChartWrap>
            </Card>

            <Grid $min='280px'>
                <Card>
                    <Label><Layers3 size={13} /> Por lote</Label>
                    <Batches>
                        {event.batches.map(batch => (
                            <Batch key={batch.name}>
                                <BatchName>{batch.name}</BatchName>
                                <BatchTrack>
                                    <BatchFill style={{ width: `${(batch.tickets / maxBatch) * 100}%` }} />
                                </BatchTrack>
                                <BatchValue>{batch.tickets}</BatchValue>
                            </Batch>
                        ))}
                    </Batches>
                </Card>
                <Card>
                    <Label><TicketPercent size={13} /> Cupons usados</Label>
                    {event.coupons.length === 0 ? (
                        <Muted>Nenhum cupom usado neste evento.</Muted>
                    ) : (
                        <Coupons>
                            {event.coupons.map(coupon => (
                                <Coupon key={coupon.code}>
                                    <code>{coupon.code}</code>
                                    <span>{coupon.orders} {coupon.orders === 1 ? 'pedido' : 'pedidos'}</span>
                                </Coupon>
                            ))}
                        </Coupons>
                    )}
                </Card>
            </Grid>
        </Section>
    );
}

function Kpi({ icon: IconComponent, label, value }) {
    return (
        <Card>
            <KpiTop>
                <IconComponent size={15} color={panelAccent(colors.secondaryPurple)} />
                <span>{label}</span>
            </KpiTop>
            <KpiValue>{value}</KpiValue>
        </Card>
    );
}

/**
 * O canvas do chart.js não entende `var(--panel-accent)`, então a cor do
 * painel (definida no body pelo DashHeader) é lida na hora de montar o gráfico.
 */
function resolvedAccent() {
    try {
        const value = getComputedStyle(document.body).getPropertyValue('--panel-accent').trim();
        return /^#[0-9a-f]{6}$/i.test(value) ? value : colors.legacyOccasionalPurple;
    } catch (error) {
        return colors.legacyOccasionalPurple;
    }
}

/**
 * Série contínua do primeiro dia com venda (ou com post) até o fim do evento,
 * com zero nos dias sem venda: os vales são justamente o que a análise aponta.
 */
function buildChart(event) {
    const accent = resolvedAccent();
    const salesByDay = Object.fromEntries(event.daily.map(day => [day.date, day.tickets]));
    const postDays = new Set(event.announcement_posts);
    const knownDays = [...event.daily.map(day => day.date), ...event.announcement_posts].sort();

    const days = [];

    if (knownDays.length) {
        let cursor = dayjs(knownDays[0]);
        const end = dayjs(event.ended_on);

        while (!cursor.isAfter(end, 'day')) {
            days.push(cursor.format('YYYY-MM-DD'));
            cursor = cursor.add(1, 'day');
        }
    }

    const tickets = days.map(day => salesByDay[day] || 0);

    return {
        data: {
            labels: days.map(day => dayjs(day).format('DD/MM')),
            datasets: [
                {
                    label: 'Post de divulgação',
                    data: days.map((day, index) => (postDays.has(day) ? tickets[index] : null)),
                    showLine: false,
                    pointStyle: 'rectRot',
                    pointRadius: 7,
                    pointHoverRadius: 8,
                    pointBackgroundColor: POST_MARKER_COLOR,
                    pointBorderColor: colors.neutralLight,
                    pointBorderWidth: 2,
                    fill: false,
                },
                {
                    label: 'Ingressos',
                    data: tickets,
                    backgroundColor: `${accent}1F`,
                    borderColor: accent,
                    borderWidth: 2,
                    fill: true,
                    lineTension: 0.25,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    pointHitRadius: 8,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            scales: {
                yAxes: [{
                    ticks: { beginAtZero: true, precision: 0, fontColor: '#999', fontSize: 12 },
                    gridLines: { color: '#f0f0f0', drawBorder: false },
                }],
                xAxes: [{
                    ticks: { fontColor: '#999', fontSize: 11, maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
                    gridLines: { display: false },
                }],
            },
            tooltips: {
                mode: 'index',
                intersect: false,
                backgroundColor: '#1A1A1A',
                cornerRadius: 8,
                xPadding: 12,
                yPadding: 10,
                filter: (item) => item.yLabel !== null && item.yLabel !== undefined && !Number.isNaN(item.yLabel),
                callbacks: {
                    label: (item) => (item.datasetIndex === 0 ? ' Post de divulgação neste dia' : ` ${item.yLabel} ingressos`),
                },
            },
        },
    };
}

const Insight = styled(Card)`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 28px;
    border-color: ${panelAccentAlpha(colors.secondaryPurple, '55')};

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

const InsightTitle = styled.p`
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const KpiTop = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${colors.night};
`;

const KpiValue = styled.div`
    margin-top: 8px;
    font-size: 22px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ChartHead = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 10px;
`;

const Legend = styled.div`
    display: flex;
    gap: 14px;
`;

const LegendItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${colors.night};
`;

const Swatch = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 2px;
    transform: ${({ $diamond }) => ($diamond ? 'rotate(45deg)' : 'none')};
`;

const ChartWrap = styled.div`
    height: 260px;
    width: 100%;
`;

const Batches = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 6px;
`;

const Batch = styled.div`
    display: grid;
    grid-template-columns: minmax(110px, 1.2fr) 2fr 32px;
    gap: 10px;
    align-items: center;
`;

const BatchName = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const BatchTrack = styled.div`
    height: 8px;
    border-radius: 8px;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const BatchFill = styled.div`
    height: 100%;
    border-radius: 8px;
    background: ${panelAccent(colors.secondaryPurple)};
`;

const BatchValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    text-align: right;
    color: ${colors.nightDark};
`;

const Coupons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 6px;
`;

const Coupon = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    color: ${colors.night};

    code {
        font-family: inherit;
        font-weight: 600;
        padding: 3px 8px;
        border-radius: 6px;
        border: 1px dashed ${panelAccentAlpha(colors.secondaryPurple, '66')};
        color: ${panelAccent(colors.secondaryPurple)};
    }
`;
